import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import axios from 'axios';
import { useSearch } from '../../components/Layout';
import { Badge, Button, Card, EmptyState, Field, HeroPill, Input, List, ListItem, MobileHero, MobilePage, SectionHeader, Select, Skeleton, StatCard } from '../../components/mobile/MobilePageKit';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const formatDate = (date) => date ? new Date(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' }) : '—';
const initials = (client) => `${client?.firstName?.[0] || ''}${client?.lastName?.[0] || ''}`.toUpperCase() || 'CL';
const displayValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    const parts = value.map(displayValue).filter(part => part && part !== '—');
    return parts.length ? parts.join(', ') : '—';
  }
  if (typeof value === 'object') {
    const preferred = value.label ?? value.name ?? value.title ?? value.value ?? value.text ?? value.answer ?? value.score ?? value.severity;
    if (preferred !== undefined && preferred !== value) return displayValue(preferred);
    const parts = Object.entries(value)
      .filter(([, entryValue]) => entryValue !== null && entryValue !== undefined && entryValue !== '')
      .map(([key, entryValue]) => `${key}: ${displayValue(entryValue)}`);
    return parts.length ? parts.join(', ') : '—';
  }
  return String(value);
};
const riskLevel = (risk) => typeof risk?.level === 'string' ? risk.level : 'low';
const getResponseValue = (responses, question, index = 0) => {
  if (!responses) return '—';
  const parsed = typeof responses === 'string' ? (() => { try { return JSON.parse(responses); } catch { return responses; } })() : responses;
  const candidates = [question?.id, question?.questionId, question?.question_id, question?.itemId, question?.order, index + 1]
    .filter(value => value !== null && value !== undefined && value !== '');

  if (Array.isArray(parsed)) {
    const found = parsed.find(response => {
      if (!response || typeof response !== 'object') return false;
      return candidates.some(candidate => [response.questionId, response.question_id, response.id, response.itemId, response.order]
        .some(value => String(value) === String(candidate)));
    });
    if (found) return displayValue(found.value ?? found.answer ?? found.response ?? found);
    return parsed[index] !== undefined ? displayValue(parsed[index]?.value ?? parsed[index]) : '—';
  }

  if (parsed && typeof parsed === 'object') {
    for (const candidate of candidates) {
      if (Object.prototype.hasOwnProperty.call(parsed, candidate)) return displayValue(parsed[candidate]);
      const stringCandidate = String(candidate);
      if (Object.prototype.hasOwnProperty.call(parsed, stringCandidate)) return displayValue(parsed[stringCandidate]);
    }
  }

  return '—';
};
const normalizeResultRecord = (result) => {
  if (!result || typeof result !== 'object') return result;
  return {
    ...result,
    Client: result.Client || result.client || null,
    TestCatalog: result.TestCatalog || result.test || null,
    TestAssignment: result.TestAssignment || result.assignment || null,
  };
};
const SBHERS_SCORE_META = {
  total: ['Overall Substance and Behavioural Risk Index (OSBRI)', 100],
  exposure_breadth: ['Exposure Breadth Index (EBI)', 21],
  current_involvement: ['Current Involvement Index (CII)', 84],
  social_environmental_exposure: ['Social/Environmental Exposure Index (SEEI)', 40],
  coping_reinforcement_risk: ['Coping and Reinforcement Risk Index (CRRI)', 40],
  emerging_loss_of_control: ['Emerging Loss-of-Control Index (ELCI)', 48],
  functional_consequences: ['Functional Consequence Index (FCI)', 24],
  protective_resources: ['Protective Resources Index (PRI)', 32],
};
const getScoreEntries = (result) => {
  const interpreted = Object.entries(result?.interpretation || {})
    .filter(([key, value]) => key && !key.startsWith('_') && value && typeof value === 'object' && 'score' in value);
  if (result?.TestCatalog?.shortName !== 'SBHERS') return interpreted;
  return Object.entries(SBHERS_SCORE_META)
    .filter(([key]) => result?.scores?.[key]?.final !== undefined)
    .map(([key, [label, maxScore]]) => [key, {
      ...(result?.interpretation?.[key] || {}),
      label,
      maxScore,
      score: result.scores[key].final,
      severity: result?.interpretation?.[key]?.severity || (key === 'protective_resources' ? 'Reported separately' : 'Component score'),
    }]);
};
const getClusterEntries = (result) => Object.entries(result?.scores?._mseClusterAnalysis?.clusters || {});
const getClientInfoRows = (result) => [
  ['Name', `${result?.Client?.firstName || ''} ${result?.Client?.lastName || ''}`.trim()],
  ['Age', result?.Client?.age],
  ['Gender', result?.Client?.gender || result?.Client?.sex],
  ['Email', result?.Client?.email],
  ['Phone', result?.Client?.phone],
  ['Occupation', result?.Client?.occupation],
  ['Education', result?.Client?.education],
  ['Marital Status', result?.Client?.maritalStatus],
  ['Date', formatDate(result?.completedAt)],
];

const MobileResults = () => {
  const { searchQuery } = useSearch();
  const [results, setResults] = useState([]);
  const [tests, setTests] = useState([]);
  const [testFilter, setTestFilter] = useState('');
  const [resultFilter, setResultFilter] = useState('all');
  const [orgNameQuery, setOrgNameQuery] = useState('');
  const [viewMode, setViewMode] = useState('individual');
  const [populationTestFilter, setPopulationTestFilter] = useState('');
  const [populationResults, setPopulationResults] = useState([]);
  const [populationLoading, setPopulationLoading] = useState(false);
  const [multiTestResults, setMultiTestResults] = useState(null);
  const [selectedTestIdx, setSelectedTestIdx] = useState(0);
  const [selectedResult, setSelectedResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [testsRes, resultsRes] = await Promise.all([
          axios.get(`${API_URL}/practitioner/tests`),
          axios.get(`${API_URL}/practitioner/results`, { params: { limit: 1000 } }),
        ]);
        setTests(testsRes.data || []);
        setResults((resultsRes.data.results || []).map(normalizeResultRecord));
      } catch (error) {
        console.error('Mobile results load error:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => { loadPopulationResults(populationTestFilter); }, [populationTestFilter]);

  useEffect(() => {
    document.body.classList.toggle('result-detail-open', Boolean(selectedResult));

    if (!selectedResult) return () => document.body.classList.remove('result-detail-open');

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.classList.remove('result-detail-open');
    };
  }, [selectedResult]);


  const filteredResults = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return results.filter(result => {
      const isOrg = result.TestAssignment?.linkType === 'organization';
      const matchesScope = resultFilter === 'all' || (resultFilter === 'org' ? isOrg : !isOrg);
      const matchesTest = !testFilter || String(result.TestCatalog?.id || result.testId) === String(testFilter);
      const matchesOrg = !orgNameQuery.trim() || (result.TestAssignment?.organizationName || '').toLowerCase().includes(orgNameQuery.toLowerCase().trim());
      const haystack = `${result.Client?.firstName || ''} ${result.Client?.lastName || ''} ${result.Client?.email || ''} ${result.TestCatalog?.shortName || ''} ${result.TestCatalog?.name || ''} ${result.TestAssignment?.organizationName || ''}`.toLowerCase();
      return matchesScope && matchesTest && matchesOrg && haystack.includes(q);
    });
  }, [results, searchQuery, testFilter, resultFilter, orgNameQuery]);

  const loadResultDetail = async (result) => {
    setSelectedResult(normalizeResultRecord(result));
    setDetailLoading(true);
    setMultiTestResults(null);
    try {
      const detailRes = await axios.get(`${API_URL}/practitioner/results/${result.id}`);
      const detail = normalizeResultRecord(detailRes.data.result || detailRes.data || result);
      setSelectedResult(detail);
      const assignmentId = detail.TestAssignment?.id || detail.assignmentId || result.TestAssignment?.id || result.assignmentId;
      if (assignmentId) {
        try {
          const multiRes = await axios.get(`${API_URL}/tests/results/${assignmentId}`);
          if (multiRes.data?.results?.length > 1) {
            const normalizedResults = multiRes.data.results.map(normalizeResultRecord);
            setMultiTestResults(normalizedResults);
            setSelectedResult(normalizedResults[0]);
            setSelectedTestIdx(0);
          }
        } catch (error) {
          console.error('Mobile multi-result load error:', error);
        }
      }
    } catch (error) {
      console.error('Mobile result detail error:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.parentElement.removeChild(link);
  };

  const downloadReport = async () => {
    if (!selectedResult?.id) return;
    try {
      const res = await axios.get(`${API_URL}/payments/download-report/${selectedResult.id}`, { responseType: 'blob' });
      downloadBlob(new Blob([res.data], { type: 'application/pdf' }), `${selectedResult.TestCatalog?.shortName || 'Assessment'}_Report_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      alert(error.response?.data?.error || error.message || 'Failed to download report.');
    }
  };

  const downloadAllReports = async () => {
    if (!multiTestResults?.length) return;
    try {
      setDownloadingAll(true);
      for (let i = 0; i < multiTestResults.length; i += 1) {
        const result = multiTestResults[i];
        const res = await axios.get(`${API_URL}/payments/download-report/${result.id}`, { responseType: 'blob' });
        downloadBlob(new Blob([res.data], { type: 'application/pdf' }), `${result.TestCatalog?.shortName || 'Assessment'}_Report_${new Date().toISOString().split('T')[0]}.pdf`);
        if (i < multiTestResults.length - 1) await new Promise(resolve => setTimeout(resolve, 400));
      }
    } finally {
      setDownloadingAll(false);
    }
  };

  const loadPopulationResults = async (testId) => {
    if (!testId) { setPopulationResults([]); return; }
    try {
      setPopulationLoading(true);
      const res = await axios.get(`${API_URL}/practitioner/results`, { params: { testId, limit: 1000 } });
      setPopulationResults(res.data.results || []);
    } catch (error) {
      console.error('Mobile population results load error:', error);
      setPopulationResults([]);
    } finally {
      setPopulationLoading(false);
    }
  };

  const exportRawData = (format = 'csv') => {
    if (!populationResults.length) { alert('No data to export'); return; }
    const selectedTest = tests.find(test => String(test.id) === String(populationTestFilter));
    const questions = selectedTest?.questions || populationResults[0]?.TestCatalog?.questions || [];
    const headers = ['Client', 'Email', 'Completed', ...questions.map((question, idx) => question.text || `Q${idx + 1}`)];
    const rows = populationResults.map(result => [
      `${result.Client?.firstName || ''} ${result.Client?.lastName || ''}`.trim(),
      result.Client?.email || '',
      result.completedAt ? new Date(result.completedAt).toISOString().split('T')[0] : '',
      ...questions.map(question => getResponseValue(result.responses, question)),
    ]);
    const delimiter = format === 'csv' ? ',' : '\t';
    const extension = format === 'csv' ? 'csv' : format === 'stata' ? 'tsv' : 'txt';
    const content = [headers, ...rows].map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(delimiter)).join('\n');
    downloadBlob(new Blob([content], { type: format === 'csv' ? 'text/csv;charset=utf-8;' : 'text/plain;charset=utf-8;' }), `${selectedTest?.shortName || 'Population'}_RawData_${new Date().toISOString().split('T')[0]}.${extension}`);
  };

  const paidCount = results.filter(result => result.isPaid).length;

  return (
    <ResultsPage>
      <CompactHero>
        <HeroPill>Assessment Analytics</HeroPill>
        <h2>Client Results</h2>
        <p>View assessment outcomes, download reports, and export population-level raw data.</p>
      </CompactHero>

      <StatsGrid>
        <CompactStatCard $accent="#2563eb"><span>Total Results</span><strong>{results.length}</strong><small>Completed</small></CompactStatCard>
        <CompactStatCard $accent="#059669"><span>Reports Paid</span><strong>{paidCount}</strong><small>Downloads</small></CompactStatCard>
        <CompactStatCard $accent="#7c3aed"><span>Active Tests</span><strong>{tests.length}</strong><small>Catalog</small></CompactStatCard>
        <CompactStatCard $accent="#d97706"><span>Visible</span><strong>{filteredResults.length}</strong><small>Filtered</small></CompactStatCard>
      </StatsGrid>

      <ToggleRow><button type="button" className={viewMode === 'individual' ? 'active' : ''} onClick={() => setViewMode('individual')}>Individual Results</button><button type="button" className={viewMode === 'population' ? 'active' : ''} onClick={() => setViewMode('population')}>Population Data</button></ToggleRow>

      {loading ? <Skeleton $height="320px" /> : viewMode === 'individual' ? (
        <>
          <CompactCard>
            <SectionHeader><div><span>Filters</span><h3>{filteredResults.length} result{filteredResults.length === 1 ? '' : 's'}</h3></div></SectionHeader>
            <FilterStack>
              <Field>Test<Select value={testFilter} onChange={event => setTestFilter(event.target.value)}><option value="">All tests</option>{tests.map(test => <option key={test.id} value={test.id}>{test.shortName || test.name}</option>)}</Select></Field>
              <Field>Org name search<Input value={orgNameQuery} onChange={event => setOrgNameQuery(event.target.value)} placeholder="Enter organization name" /></Field>
              <TabRow><button type="button" className={resultFilter === 'all' ? 'active' : ''} onClick={() => setResultFilter('all')}>All</button><button type="button" className={resultFilter === 'direct' ? 'active' : ''} onClick={() => setResultFilter('direct')}>Direct</button><button type="button" className={resultFilter === 'org' ? 'active' : ''} onClick={() => setResultFilter('org')}>Org</button></TabRow>
            </FilterStack>
          </CompactCard>

          <CompactCard>
            <SectionHeader><div><span>Latest activity</span><h3>Client results</h3></div></SectionHeader>
            {filteredResults.length === 0 ? <EmptyState><strong>No results found</strong><p>Try changing filters or search.</p></EmptyState> : (
              <List>{filteredResults.map(result => <CompactListItem key={result.id} type="button" onClick={() => loadResultDetail(result)}><Avatar>{initials(result.Client)}</Avatar><ResultCopy><strong>{result.Client?.firstName} {result.Client?.lastName}</strong><span>{result.TestCatalog?.shortName || 'Assessment'} • {formatDate(result.completedAt)} • {result.TestAssignment?.linkType === 'organization' ? result.TestAssignment?.organizationName || 'Org' : 'Direct'}</span></ResultCopy><StatusStack><Badge $tone={result.isPaid ? 'paid' : 'completed'}>{result.isPaid ? 'Paid' : 'Done'}</Badge><Badge $tone={riskLevel(result.followUpRisk)}>{riskLevel(result.followUpRisk)}</Badge></StatusStack></CompactListItem>)}</List>
            )}
          </CompactCard>
        </>
      ) : (
        <CompactCard>
          <SectionHeader><div><span>Population data</span><h3>Raw data export</h3></div></SectionHeader>
          <Field>Test<Select value={populationTestFilter} onChange={event => setPopulationTestFilter(event.target.value)}><option value="">Choose a test</option>{tests.map(test => <option key={test.id} value={test.id}>{test.shortName || test.name}</option>)}</Select></Field>
          {populationLoading ? <Skeleton $height="120px" /> : populationResults.length === 0 ? <EmptyState><strong>No population data</strong><p>Select a test with completed results to export raw response data.</p></EmptyState> : <PopulationBox><strong>{populationResults.length} completed responses</strong><span>Export raw data for SPSS, Excel, CSV, or Stata workflows.</span><ActionRow><Button type="button" onClick={() => exportRawData('csv')}>CSV</Button><Button $secondary type="button" onClick={() => exportRawData('spss')}>SPSS</Button><Button $secondary type="button" onClick={() => exportRawData('stata')}>Stata</Button></ActionRow></PopulationBox>}
        </CompactCard>
      )}

      {selectedResult && createPortal(
        <SheetBackdrop role="dialog" aria-modal="true" onClick={() => setSelectedResult(null)}>
          <ResultModal onClick={event => event.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{displayValue(selectedResult.TestCatalog?.name || selectedResult.TestCatalog?.shortName || 'Assessment')} — Results</ModalTitle>
              <ModalActions>
                {multiTestResults?.length > 1 ? <Button disabled={downloadingAll} type="button" onClick={downloadAllReports}>{downloadingAll ? 'Downloading…' : 'Download All'}</Button> : <Button type="button" onClick={downloadReport}>Download Report</Button>}
                <CloseButton type="button" onClick={() => setSelectedResult(null)}>×</CloseButton>
              </ModalActions>
            </ModalHeader>
            {detailLoading ? <Skeleton $height="140px" /> : <ModalBody>
              {multiTestResults?.length > 1 && <TabScroller>{multiTestResults.map((result, idx) => <button key={result.id || idx} type="button" className={selectedTestIdx === idx ? 'active' : ''} onClick={() => { setSelectedTestIdx(idx); setSelectedResult(result); }}>{idx + 1}. {displayValue(result.TestCatalog?.shortName || result.TestCatalog?.name)}</button>)}</TabScroller>}
              <>
                <OriginalInfoGrid>
                  {getClientInfoRows(selectedResult).map(([label, value]) => <InfoCell key={label}><span>{label}</span><strong>{displayValue(value)}</strong></InfoCell>)}
                  <InfoCell><span>Follow-up</span><strong><RiskBadge $level={riskLevel(selectedResult.followUpRisk)}>{riskLevel(selectedResult.followUpRisk)}</RiskBadge></strong></InfoCell>
                </OriginalInfoGrid>

                {selectedResult.followUpRisk?.isFlagged && <OriginalSection>
                  <SectionCaption>🚩 Follow-up Flag</SectionCaption>
                  <OriginalAlert $critical={selectedResult.followUpRisk.level === 'critical'}>
                    <strong>{displayValue(selectedResult.followUpRisk.recommendedAction)}</strong>
                    {selectedResult.followUpRisk.reasons?.length > 0 && <ul>{selectedResult.followUpRisk.reasons.map((reason, idx) => <li key={idx}>{displayValue(reason)}</li>)}</ul>}
                  </OriginalAlert>
                </OriginalSection>}

                {getScoreEntries(selectedResult).length > 0 && <OriginalScoresGrid>
                  {getScoreEntries(selectedResult).map(([key, value]) => <OriginalScoreBox key={key}><span>{displayValue(value.label || key)}</span><strong>{displayValue(value.score)}{value.maxScore ? ` / ${value.maxScore}` : ''}</strong><small>{displayValue(value.severity)}</small></OriginalScoreBox>)}
                </OriginalScoresGrid>}

                {selectedResult.interpretation?._description && <OriginalNote><strong>Test Description</strong><p>{displayValue(selectedResult.interpretation._description)}</p></OriginalNote>}
                {selectedResult.alerts?.length > 0 && <OriginalSection><SectionCaption>⚠️ Clinical Alerts</SectionCaption>{selectedResult.alerts.map((alert, idx) => <OriginalAlert key={idx}><strong>{displayValue(alert.type || 'Alert')}</strong><span>{displayValue(alert.message || alert)}</span></OriginalAlert>)}</OriginalSection>}

                {getClusterEntries(selectedResult).length > 0 && <OriginalSection>
                  <SectionCaption>🧠 MSE Diagnostic Clusters & Clinical Decision Pathways</SectionCaption>
                  <OriginalScoresGrid>{getClusterEntries(selectedResult).map(([key, cluster]) => <OriginalScoreBox key={key}><span>{displayValue(key)}</span><strong>{displayValue(cluster.standardized ?? cluster.score)}</strong><small>{displayValue(cluster.severity)}</small></OriginalScoreBox>)}</OriginalScoresGrid>
                </OriginalSection>}

                {selectedResult.interpretation?._recommendations && <OriginalNote><strong>Diagnostic Recommendations</strong><p>{displayValue(selectedResult.interpretation._recommendations)}</p></OriginalNote>}
                {selectedResult.TestCatalog?.questions?.length > 0 && <OriginalResponses><SectionCaption>📝 Individual Responses</SectionCaption>{selectedResult.TestCatalog.questions.map((question, idx) => <div key={question.id}><span>{idx + 1}. {displayValue(question.text)}</span><strong>{getResponseValue(selectedResult.responses, question, idx)}</strong></div>)}</OriginalResponses>}
              </>
            </ModalBody>}
          </ResultModal>
        </SheetBackdrop>,
        document.body
      )}

    </ResultsPage>
  );
};

const ResultsPage = styled(MobilePage)`
  gap: 10px;
`;

const CompactHero = styled(MobileHero)`
  border-radius: 20px;
  box-shadow: 0 12px 28px rgba(30, 64, 175, 0.18);
  padding: 14px;

  h2 {
    font-size: clamp(1.08rem, 6vw, 1.45rem);
    margin: 7px 0 5px;
  }

  p {
    font-size: 0.76rem;
    line-height: 1.38;
  }

  > div:first-of-type {
    font-size: 0.64rem;
    gap: 5px;
    padding: 4px 8px;
  }

  > div:first-of-type::before {
    height: 6px;
    width: 6px;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(4, minmax(0, 1fr));

  @media (max-width: 390px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const CompactStatCard = styled(StatCard)`
  border-radius: 16px;
  min-height: 72px;
  padding: 9px 8px;

  &::before {
    height: 3px;
    left: 10px;
    right: 10px;
  }

  span {
    font-size: 0.62rem;
    margin-top: 2px;
  }

  strong {
    font-size: clamp(1rem, 5vw, 1.22rem);
    margin-top: 7px;
  }

  small {
    font-size: 0.58rem;
    margin-top: 3px;
  }
`;

const CompactCard = styled(Card)`
  border-radius: 18px;
  padding: 10px;

  ${SectionHeader} {
    margin-bottom: 8px;
  }

  ${SectionHeader} span {
    font-size: 0.6rem;
  }

  ${SectionHeader} h3 {
    font-size: 0.88rem;
    margin-top: 2px;
  }
`;

const FilterStack = styled.div`
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  label {
    font-size: 0.62rem;
    gap: 5px;
    letter-spacing: 0.04em;
    min-width: 0;
    text-transform: uppercase;
  }

  input,
  select {
    border-radius: 13px;
    font-size: 16px;
    min-height: 40px;
    padding: 0 10px;
  }

  > div:last-child {
    grid-column: 1 / -1;
  }
`;

const ToggleRow = styled.div`
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid #e2e8f0;
  border-radius: 15px;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.05);
  display: grid;
  grid-template-columns: 1fr 1fr;
  padding: 3px;
  position: sticky;
  top: max(8px, env(safe-area-inset-top));
  z-index: 5;

  button {
    background: transparent;
    border: 0;
    border-radius: 12px;
    color: #64748b;
    font-size: 0.78rem;
    font-weight: 900;
    min-height: 38px;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }

  .active {
    background: linear-gradient(135deg, #0284c7, #1e40af);
    color: white;
  }
`;

const TabRow = styled.div`
  display: grid;
  gap: 6px;
  grid-template-columns: repeat(3, 1fr);

  button {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 13px;
    color: #64748b;
    font-size: 0.72rem;
    font-weight: 900;
    min-height: 38px;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }

  .active {
    background: #dbeafe;
    border-color: #93c5fd;
    color: #075985;
  }
`;

const CompactListItem = styled(ListItem)`
  border-radius: 16px;
  gap: 9px;
  min-height: 60px;
  padding: 9px;
`;

const Avatar = styled.div`
  align-items: center;
  background: linear-gradient(135deg, #2563eb, #0ea5e9);
  border-radius: 14px;
  color: white;
  display: flex;
  flex: 0 0 auto;
  font-size: 0.72rem;
  font-weight: 950;
  height: 38px;
  justify-content: center;
  width: 38px;
`;

const ResultCopy = styled.div`
  flex: 1;
  min-width: 0;

  strong {
    color: #0f172a;
    display: block;
    font-size: 0.82rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  span {
    color: #64748b;
    display: block;
    font-size: 0.66rem;
    margin-top: 3px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const StatusStack = styled.div`
  align-items: flex-end;
  display: flex;
  flex-direction: column;
  gap: 4px;

  span {
    font-size: 0.58rem;
    padding: 4px 7px;
  }
`;
const SheetBackdrop = styled.div`background: rgba(15, 23, 42, 0.36); inset: 0; position: fixed; z-index: 2000;`;
const ResultModal = styled.section`
  background: white;
  border-radius: 14px;
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.28);
  display: flex;
  flex-direction: column;
  left: max(10px, env(safe-area-inset-left));
  max-height: calc(100vh - max(20px, env(safe-area-inset-top)) - max(20px, env(safe-area-inset-bottom)));
  overflow: hidden;
  position: fixed;
  right: max(10px, env(safe-area-inset-right));
  top: max(10px, env(safe-area-inset-top));
`;

const ModalHeader = styled.div`
  align-items: center;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  gap: 10px;
  justify-content: space-between;
  padding: 11px 12px;
`;

const ModalTitle = styled.h3`
  color: #0f172a;
  font-size: 0.9rem;
  line-height: 1.25;
  margin: 0;
`;

const ModalActions = styled.div`
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  gap: 6px;
  button:not(:last-of-type) { min-height: 34px; padding: 8px 10px; }
`;

const CloseButton = styled.button`
  align-items: center;
  background: #f8fafc;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  color: #64748b;
  display: flex;
  font-size: 1.2rem;
  font-weight: 800;
  height: 34px;
  justify-content: center;
  width: 34px;
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
  padding: 12px;
  -webkit-overflow-scrolling: touch;
`;

const OriginalInfoGrid = styled.div`
  background: #eaf6ff;
  border: 1px solid #93c5fd;
  border-radius: 10px;
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  padding: 10px;
`;

const InfoCell = styled.div`
  min-width: 0;
  span { color: #2563eb; display: block; font-size: 0.62rem; font-weight: 950; letter-spacing: 0.05em; text-transform: uppercase; }
  strong { color: #0f172a; display: block; font-size: 0.74rem; margin-top: 3px; overflow-wrap: anywhere; }
`;

const RiskBadge = styled.span`
  background: ${p => (p.$level === 'critical' ? '#fee2e2' : p.$level === 'high' ? '#ffedd5' : '#dcfce7')};
  border: 1px solid ${p => (p.$level === 'critical' ? '#ef4444' : p.$level === 'high' ? '#fb923c' : '#22c55e')};
  border-radius: 999px;
  color: ${p => (p.$level === 'critical' ? '#b91c1c' : p.$level === 'high' ? '#9a3412' : '#166534')};
  display: inline-flex;
  font-size: 0.64rem;
  font-weight: 950;
  padding: 3px 8px;
  text-transform: capitalize;
`;

const OriginalSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SectionCaption = styled.h4`
  color: #0f172a;
  font-size: 0.8rem;
  font-weight: 950;
  margin: 0;
`;

const OriginalAlert = styled.div`
  background: ${p => (p.$critical ? '#fee2e2' : '#fff7ed')};
  border-left: 3px solid ${p => (p.$critical ? '#dc2626' : '#f59e0b')};
  border-radius: 8px;
  color: ${p => (p.$critical ? '#991b1b' : '#92400e')};
  font-size: 0.75rem;
  line-height: 1.45;
  padding: 10px;
  strong { display: block; margin-bottom: 4px; }
  ul { margin: 6px 0 0 18px; padding: 0; }
`;

const OriginalScoresGrid = styled.div`
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
`;

const OriginalScoreBox = styled.div`
  border: 1px solid #dbeafe;
  border-radius: 10px;
  padding: 10px;
  span { color: #64748b; display: block; font-size: 0.62rem; font-weight: 950; text-transform: uppercase; }
  strong { color: #0284c7; display: block; font-size: 1rem; margin-top: 5px; }
  small { background: #dcfce7; border-radius: 999px; color: #166534; display: inline-flex; font-size: 0.62rem; font-weight: 900; margin-top: 5px; padding: 3px 7px; }
`;

const OriginalNote = styled.section`
  background: #eff6ff;
  border-left: 3px solid #0ea5e9;
  border-radius: 8px;
  color: #0f172a;
  padding: 10px;
  strong { display: block; font-size: 0.78rem; margin-bottom: 5px; }
  p { color: #334155; font-size: 0.76rem; line-height: 1.5; margin: 0; }
`;

const OriginalResponses = styled.section`
  display: flex;
  flex-direction: column;
  gap: 7px;
  div { align-items: flex-start; background: #f8fafc; border-radius: 8px; display: grid; gap: 8px; grid-template-columns: 1fr auto; padding: 8px; }
  span { color: #334155; font-size: 0.74rem; line-height: 1.35; }
  strong { background: #dbeafe; border-radius: 6px; color: #1d4ed8; font-size: 0.72rem; min-width: 28px; padding: 4px 7px; text-align: center; }
`;

const ActionRow = styled.div`display: flex; gap: 8px; margin-top: 10px; button { flex: 1; }`;
const PopulationBox = styled.div`background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 18px; margin-top: 12px; padding: 14px; strong { color: #0f172a; display: block; } span { color: #64748b; display: block; font-size: 0.8rem; margin-top: 5px; }`;
const TabScroller = styled.div`display: flex; gap: 7px; overflow-x: auto; button { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 999px; color: #64748b; flex: 0 0 auto; font-weight: 900; padding: 9px 12px; } .active { background: #dbeafe; color: #075985; }`;

export default MobileResults;
